import { useState } from 'react';
import { motion } from 'motion/react';
import { Link, useNavigate } from 'react-router-dom';
import { UserPlus, Loader2 } from 'lucide-react';
import { useAuth } from '../../lib/auth/AuthContext';
import { auth, updateProfile } from '../../lib/db/firebase_auth';
import { config } from '../../lib/config';
import { Button } from '../../components/ui/Button';
import { Field, Input } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toaster';

export function RegisterScreen() {
  const { registerEmail, sendVerificationEmail } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast('Kata sandi terlalu pendek', { body: 'Gunakan minimal 6 karakter.', tone: 'error' });
      return;
    }
    if (password !== confirm) {
      toast('Kata sandi tidak cocok', { body: 'Pastikan kedua kata sandi sama.', tone: 'error' });
      return;
    }
    setBusy(true);
    try {
      await registerEmail(email, password);
      // Prefill the display name Firebase will associate with the account.
      const fbUser = auth.currentUser;
      if (fbUser && name) {
        try {
          await updateProfile(fbUser, { displayName: name });
        } catch {
          /* noop */
        }
      }
      try {
        await sendVerificationEmail();
      } catch {
        /* noop */
      }
      toast('Akun dibuat', {
        body: 'Silakan verifikasi email Anda untuk melanjutkan.',
        tone: 'success',
      });
      navigate('/verify-email', { replace: true });
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? '';
      let body = 'Terjadi kesalahan saat mendaftar.';
      if (code === 'auth/email-already-in-use') body = 'Email sudah terdaftar. Silakan masuk.';
      else if (code === 'auth/invalid-email') body = 'Format email tidak valid.';
      else if (code === 'auth/weak-password') body = 'Kata sandi terlalu lemah.';
      toast('Gagal mendaftar', { body, tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-[460px] flex-col justify-center px-6 pb-10 pt-[max(env(safe-area-inset-top),3rem)]">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="mb-8 flex items-center gap-3">
          <span className="grid size-12 place-items-center rounded-none bg-accent text-[22px] font-black text-white">
            B
          </span>
          <div>
            <h1 className="display text-[26px] font-bold text-ink">Bimbingan</h1>
            <p className="text-[13px] text-muted">{config.appTagline}</p>
          </div>
        </div>

        {config.firebaseConfigured ? (
          <div className="card p-5">
            <div className="mb-5 flex items-center gap-2.5">
              <span className="grid size-10 place-items-center rounded-none bg-accent-soft text-accent-strong">
                <UserPlus className="size-5" />
              </span>
              <div>
                <h2 className="text-[17px] font-bold text-ink">Buat akun baru</h2>
                <p className="text-[13px] text-muted">Daftar dengan email institusi Anda.</p>
              </div>
            </div>

            <form onSubmit={submit} className="space-y-3">
              <Field label="Nama lengkap">
                <Input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nama Anda"
                  autoComplete="name"
                />
              </Field>
              <Field label="Email">
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nama@univ.ac.id"
                  autoComplete="email"
                />
              </Field>
              <Field label="Kata sandi">
                <Input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimal 6 karakter"
                  autoComplete="new-password"
                />
              </Field>
              <Field label="Ulangi kata sandi">
                <Input
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </Field>
              <Button block type="submit" disabled={busy}>
                {busy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <UserPlus className="size-4" />
                )}
                Daftar
              </Button>
            </form>

            <p className="mt-5 text-center text-[13px] text-muted">
              Sudah punya akun?{' '}
              <Link to="/login" className="font-semibold text-accent">
                Masuk
              </Link>
            </p>
          </div>
        ) : (
          <div className="card p-6 text-center">
            <h2 className="text-[16px] font-bold text-ink">Konfigurasi belum lengkap</h2>
            <p className="mt-1 text-[13px] leading-relaxed text-muted">
              Firebase Authentication belum dikonfigurasi pada lingkungan ini. Periksa variabel
              lingkungan VITE_FIREBASE_*.
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
